import axios from "axios";
import { AppUser } from "../TimeTracking/types";

// Log environment variables for debugging
//console.log("🔹 API Base URL:", process.env.EXPO_PUBLIC_API_URL);
//console.log("🔹 API Username:", process.env.EXPO_PUBLIC_USERNAME);

let bearerToken = "";



export const setBearerToken = (token: string) => {
  bearerToken = token;
};

// Create Axios instance with Base URL & Headers
const api = axios.create({
  baseURL: process.env.EXPO_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json"
  },
});


// Add Bearer token before each request
api.interceptors.request.use((config) => {
  if (bearerToken) {
    if (config.headers) {
      config.headers.Authorization = `Bearer ${bearerToken}`;
    }
  }
  return config;
});

// Log the default headers
//console.log("🛠 Axios Default Headers:", api.defaults.headers);

/**
 * Registers a new time entry in the backend.
 * @param userId User's ID
 * @param type Entry type (ClockIn, ClockOut, BreakIn, BreakOut, LunchIn, LunchOut)
 * @param includeTime Optional flag to include time in the payload (only for requests)
 */
export const postEntry = async (
  userId: number,
  type: string,
  includeTime = false,
  tasks?: string
) => {
  try {
    const payload: any = {
      userId,
      type,
      tasks: tasks && tasks.trim() !== ""
        ? tasks
        : "{\"task1\": \"description1\", \"task2\": \"description2\"}",
    };

    // Si `includeTime` es verdadero, agregamos la fecha al payload
    if (includeTime) {
      payload.time = new Date().toISOString();
    }

    console.log("📤 Sending POST request to /entries", payload);

    const response = await api.post("/entries", payload);

    console.log("✅ POST Response:", response.status, response.data);
    return response.data;
  } catch (error: any) {
    console.error(`❌ Error posting entry (${type}):`, error.message);

    if (error.response) {
      console.error("📥 Response Data:", error.response.data);
      console.error("📥 Response Status:", error.response.status);
      console.error("📥 Response Headers:", error.response.headers);
    } else if (error.request) {
      console.error("📡 No response received:", error.request);
    } else {
      console.error("⚠️ Unexpected Error:", error.message);
    }

    throw error;
  }
};
/**
 * Fetches the user's time entries for today.
 * @param userId User's ID
 */
export const getEntries = async (userId: number): Promise<{ time: string; type: string }[]> => {
    const today = new Date().toISOString().split("T")[0]; // Obtener la fecha actual
    const startOfDay = `${today}T00:00:00Z`;
    const endOfDay = `${today}T23:59:59Z`;
  
    try {
      console.log("📤 Fetching entries for user:", userId);
      console.log("📅 Date Range:", { after: startOfDay, before: endOfDay });
  
      const response = await api.get(`/entries/${userId}`, {
        params: {
          before: endOfDay,
          after: startOfDay,
          type: "",
          offset: 0,
          limit: 50, // Aseguramos que trae suficientes registros
        },
      });
  
      console.log("✅ GET Response:", response.status, response.data);
  
      // Verificar si `response.data` es un array
      if (Array.isArray(response.data)) {
        return response.data.map((entry: any) => ({
          time: entry.time || new Date().toISOString(), // Asegurar que la hora no sea `undefined`
          type: entry.type || "Unknown", // Manejar valores nulos
        }));
      } else {
        console.warn("⚠️ Unexpected API response format:", response.data);
        return [];
      }
    } catch (error: any) {
      console.error("❌ Error fetching entries:", error.message);
      
      if (error.response) {
        console.error("📥 Response Data:", error.response.data);
        console.error("📥 Response Status:", error.response.status);
        console.error("📥 Response Headers:", error.response.headers);
      } else if (error.request) {
        console.error("📡 No response received:", error.request);
      } else {
        console.error("⚠️ Unexpected Error:", error.message);
      }
  
      return []; // Retorna un array vacío en caso de error
    }
};


export const getUserIdByEmail = async (email: string): Promise<AppUser | null> => {
  console.log("📥 ENTERED getUserIdByEmail");

  try {
    const response = await api.get<AppUser>(`/users`, {
      params: { email }
    });

    console.log("📥 Raw response from /users:", response.data);

    const user = response.data;

    if (user && user.id) {
      console.log("✅ Found user:", user);
      return user;
    }

    console.warn("⚠️ User not found for email:", email);
    return null;
  } catch (error: any) {
    if (error.response) {
      console.error("❌ Axios error response:", error.response.status, error.response.data);
    } else if (error.request) {
      console.error("❌ Axios no response received:", error.request);
    } else {
      console.error("❌ Unexpected error:", error.message);
    }
    return null;
  }
};


type DailyMinutesResponse = {
  dailyMinutes: {
    [date: string]: {
      minutesWorked: number;
      minutesLunchBreak: number;
      minutesRegularBreak: number;
    };
  };
  totalMinutesWorked: number;
  totalMinutesLunchBreak: number;
  totalMinutesRegularBreak: number;
};

export const getHoursByRange = async (
  appUserId: number,
  after?: string,
  before?: string,
  period?: string
): Promise<DailyMinutesResponse> => {
  try {
    const params: Record<string, string> = {};
    if (after) params.after = after;
    if (before) params.before = before;
    if (period) params.period = period;

    const response = await api.get<DailyMinutesResponse>(
      `/entries/hours/${appUserId}`,
      { params }
    );

    console.log("✅ GET Response:", response.status, response.data);

    return response.data;
  } catch (error: any) {
    console.error("❌ Error fetching hours by range:", error.message);
    throw error;
  }
};
/**
 * Fetches users currently working (active sessions).
 * Endpoint: /users/working
 */
export const getWorkingUsers = async (): Promise<AppUser[]> => {
  try {
    console.log("📤 Fetching working users...");

    const response = await api.get<AppUser[]>("/users/working");

    console.log("✅ GET /users/working Response:", response.status, response.data);

    if (Array.isArray(response.data)) {
      return response.data;
    } else {
      console.warn("⚠️ Unexpected format in working users response:", response.data);
      return [];
    }
  } catch (error: any) {
    console.error("❌ Error fetching working users:", error.message);

    if (error.response) {
      console.error("📥 Response Data:", error.response.data);
      console.error("📥 Response Status:", error.response.status);
    } else if (error.request) {
      console.error("📡 No response received:", error.request);
    } else {
      console.error("⚠️ Unexpected Error:", error.message);
    }

    return [];
  }
};
/**
 * Fetches all user time entries.
 */
export const getUserReport = async (userId: number, [from, to]: [string, string]) => {
  const fromDate = `${from}T00:00:00`;
  const toDate = `${to}T23:59:59`;
  const response = await api.get(`/entries/${userId}`, {
    params: { after: fromDate, before: toDate, limit: 100 }
  });
  return response.data;
};

export const getManagedUsers = async (): Promise<AppUser[]> => {
  try {
    const response = await api.get<AppUser[]>("/entry-requests/managees");
    return response.data;
  } catch (error: any) {
    console.error("❌ Error fetching managed users:", error.message);

    if (error.response) {
      console.error("📥 Response Data:", error.response.data);
      console.error("📥 Response Status:", error.response.status);
    } else if (error.request) {
      console.error("📡 No response received:", error.request);
    } else {
      console.error("⚠️ Unexpected Error:", error.message);
    }

    return [];
  }
};

export const getEntryRequests = async (offset = 0, limit = 100) => {
  const response = await api.get(`/entry-requests/approvals`, { params: { offset, limit } });
  return response.data;
};

export const manageEntryRequest = async (id: number, action: "approve" | "deny") => {
  return api.post(`/entry-requests/${id}/${action}`);
};


type EntryRequestPayload = {
  type: "ClockIn" | "ClockOut" | "BreakIn" | "BreakOut" | "LunchIn" | "LunchOut";
  time: string;
  entryId?: number;
  userId: number;
};

export const postEntryRequest = async (payload: EntryRequestPayload) => {
  try {
    console.log("📤 Sending POST /entry-requests/", payload);
    const response = await api.post("/entry-requests/", payload);
    console.log("✅ Entry request sent:", response.status, response.data);
    return response.data;
  } catch (error: any) {
    console.error("❌ Error posting entry request:", error.message);
    if (error.response) {
      console.error("📥 Response Data:", error.response.data);
      console.error("📥 Response Status:", error.response.status);
      console.error("📥 Response Headers:", error.response.headers);
    } else if (error.request) {
      console.error("📡 No response received:", error.request);
    } else {
      console.error("⚠️ Unexpected Error:", error.message);
    }
    throw error;
  }
};

/**
 * Approves or denies a time entry request
 * @param entryRequestId ID of the entry request
 * @param action 'approve' or 'deny'
 */
export const patchEntryRequestAction = async (
  entryRequestId: number,
  action: "approve" | "deny"
): Promise<void> => {
  try {
    console.log(`🔄 Sending PATCH to /entry-requests/${entryRequestId}/${action}`);
    const response = await api.patch(`/entry-requests/${entryRequestId}/${action}`);
    console.log(`✅ Request ${action} successful. Status:`, response.status);
  } catch (error: any) {
    console.error(`❌ Failed to ${action} request:`, error.message);
    if (error.response) {
      console.error("📥 Response Data:", error.response.data);
      console.error("📥 Response Status:", error.response.status);
    } else if (error.request) {
      console.error("📡 No response received:", error.request);
    } else {
      console.error("⚠️ Unexpected Error:", error.message);
    }
    throw error;
  }
};


export default api;